
import { Link } from 'react-router-dom';
import type { NeighborhoodDetail } from '../../../../mocks/neighborhoodDetails';

interface NeighborhoodHeroProps {
  neighborhood: NeighborhoodDetail;
}

export default function NeighborhoodHero({ neighborhood }: NeighborhoodHeroProps) {
  return (
    <section className="relative h-[520px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={neighborhood.heroImage}
          alt={`${neighborhood.name} district in Roseville California`}
          title={`${neighborhood.name} Roseville CA`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
      </div>

      <div className="relative z-10 h-full flex flex-col justify-between max-w-7xl mx-auto px-6">
        {/* Breadcrumb */}
        <div className="pt-24">
          <nav className="flex items-center gap-2 text-sm text-white/70">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-white/40"></i>
            <span className="text-white/50">Neighborhoods</span>
            <i className="ri-arrow-right-s-line text-white/40"></i>
            <span className="text-white">{neighborhood.name}</span>
          </nav>
        </div>

        {/* Content */}
        <div className="pb-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div className="w-full">
              <span
                className="inline-block px-4 py-1.5 rounded-full text-xs font-bold tracking-wide uppercase mb-4"
                style={{ backgroundColor: `${neighborhood.accentColor}30`, color: neighborhood.accentColor }}
              >
                {neighborhood.tagline}
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-4">
                <a href={`https://www.google.com/maps/search/?api=1&query=${neighborhood.mapQuery}`} target="_blank" rel="noopener noreferrer nofollow" className="hover:text-white/90 transition-colors">
                  {neighborhood.name}
                </a>
              </h1>
              <p className="text-white/80 text-lg max-w-2xl leading-relaxed">
                {neighborhood.description}
              </p>

              {/* Highlight Pills */}
              <div className="flex flex-wrap gap-2 mt-6">
                {neighborhood.highlights.slice(0, 6).map((h) => (
                  <span
                    key={h}
                    className="px-3 py-1.5 bg-white/15 backdrop-blur-sm text-white text-xs font-medium rounded-full"
                  >
                    {h}
                  </span>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="flex gap-4 flex-shrink-0">
              <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-4 text-center min-w-[90px]">
                <p className="text-2xl font-black text-white">{neighborhood.businessCount}</p>
                <p className="text-[11px] text-white/70 mt-0.5">Businesses</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-4 text-center min-w-[90px]">
                <p className="text-2xl font-black text-white">{neighborhood.avgRating}</p>
                <p className="text-[11px] text-white/70 mt-0.5">Avg Rating</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-4 text-center min-w-[90px]">
                <p className="text-2xl font-black text-white">{neighborhood.diningCount}</p>
                <p className="text-[11px] text-white/70 mt-0.5">Restaurants</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
